// src/components/ModalVerOrdenPagoRemitos.jsx
import { useEffect, useState, useMemo } from "react";
import { Modal, Button, Table, Row, Col } from "react-bootstrap";
import moment from "moment";
import { getOrdenPagoRemitoById } from "../helpers/rutaOrdenPagoRemitos";
import { generarPDFOrdenPagoRemito } from "../helpers/pdfOrdenPagoRemito";

const $ = (n) =>
  Number(n || 0).toLocaleString("es-AR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

// Helper para mostrar el origen/datos del pago de forma legible
const origenPago = (p) => {
  const medio = String(p?.medio || "").toLowerCase();

  if (medio === "efectivo") {
    const desc = p?.caja?.descripcion;
    return desc ? `Caja: ${desc}` : p?.caja ? `Caja: ${p.caja}` : "-";
  }

  if (medio === "transferencia") {
    const c = p?.cuentaBancaria;
    const etiqueta = c?.alias || c?.numero || "";
    return etiqueta
      ? `Cuenta: ${etiqueta}`
      : p?.cuentaBancaria
      ? `Cuenta: ${p.cuentaBancaria}`
      : "-";
  }

  if (medio === "cheque") {
    const nro = p?.referencia || p?.nroCheque || "";
    const fv = p?.fechaCheque ? ` — ${moment(p.fechaCheque).format("DD/MM/YYYY")}` : "";
    return nro ? `Cheque ${nro}${fv}` : "-";
  }

  if (medio === "icheq") {
    return p?.referencia ? `iCheq ${p.referencia}` : "iCheq";
  }

  return "-";
};

export default function ModalVerOrdenPagoRemitos({ show, opId, onHide }) {
  const [op, setOp] = useState(null);
  const loading = !op && show;

  useEffect(() => {
    if (!show || !opId) return;
    (async () => {
      const r = await getOrdenPagoRemitoById(opId);
      setOp(r?.orden || null);
    })();
  }, [show, opId]);

  const totPagos = useMemo(
    () => (op?.pagos || []).reduce((a, m) => a + Number(m.importe || 0), 0),
    [op]
  );

  const totRets = useMemo(
    () => (op?.retenciones || []).reduce((a, r) => a + Number(r.importe || 0), 0),
    [op]
  );

  // Cliente mostrado: prioriza op.cliente. (Si además populás remito.cliente en el back,
  // podés usarlo como fallback.)
  const clienteNombre = useMemo(() => {
    if (!op) return "-";
    return (
      op?.cliente?.razonsocial ||
      op?.cliente?.nombre ||
      // fallback por si en el futuro populás remito.cliente:
      op?.aplicaciones?.find((a) => a?.remito?.cliente)?.remito?.cliente?.razonsocial ||
      "-"
    );
  }, [op]);

  return (
    <Modal show={show} onHide={() => onHide(false)} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {op
            ? op.numeroA || op.numeroB
              ? `Orden de Pago ${[op.numeroA, op.numeroB]
                  .filter(Boolean)
                  .join(" / ")}`
              : `Orden de Pago ${op.numero}`
            : "Orden de Pago"}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {!op ? (
          <div className="text-muted">Cargando...</div>
        ) : (
          <>
             <Row className="mb-2">
              <Col md={12}>
                {op.numeroA && (
                  <>
                    <b>N° Cuenta A:</b> {op.numeroA}
                    <br />
                  </>
                )}

                {op.numeroB && (
                  <>
                    <b>N° Cuenta B:</b> {op.numeroB}
                    <br />
                  </>
                )}

                {!op.numeroA && !op.numeroB && op.numero && (
                  <>
                    <b>N° Orden:</b> {op.numero}
                  </>
                )}
              </Col>
            </Row>

            <Row className="mb-2">
              <Col md={6}>
                <b>Fecha:</b> {op.fecha ? moment(op.fecha).format("DD/MM/YYYY") : "-"}
              </Col>
              <Col md={6}>
                <b>Estado:</b> {(op.estado || "pendiente").toUpperCase()}
              </Col>
            </Row>
            <Row className="mb-2">
              <Col md={6}>
                <b>Proveedor:</b> {op.proveedor?.razonsocial || op.proveedor?.nombre || "-"}
              </Col>
              <Col md={6}>
                <b>Cliente:</b> {clienteNombre}
              </Col>
            </Row>

            <h6 className="mt-3">Aplicaciones</h6>
            <Table size="sm" bordered>
              <thead>
                <tr>
                  <th>Remito</th>
                  <th style={{ textAlign: "right" }}>Pactado</th>
                  <th style={{ textAlign: "right" }}>Pagado ant.</th>
                  <th style={{ textAlign: "right" }}>Aplicado</th>
                </tr>
              </thead>
              <tbody>
                {(op.aplicaciones || []).map((a, i) => (
                  <tr key={i}>
                    <td>#{String(a.remito?.codrem || "").padStart(6, "0")}</td>
                    <td style={{ textAlign: "right" }}>${$(a.pactado)}</td>
                    <td style={{ textAlign: "right" }}>${$(a.pagadoAnterior)}</td>
                    <td style={{ textAlign: "right" }}>${$(a.aplicado)}</td>
                  </tr>
                ))}
                {!op.aplicaciones?.length && (
                  <tr>
                    <td colSpan={4} className="text-muted">
                      Sin aplicaciones
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>

            <h6 className="mt-3">Pagos</h6>
            <Table size="sm" bordered>
              <thead>
                <tr>
                  <th>Medio</th>
                  <th>Origen/Datos</th>
                  <th style={{ textAlign: "right" }}>Importe</th>
                </tr>
              </thead>
              <tbody>
                {(op.pagos || []).map((p, i) => (
                  <tr key={i}>
                    <td>{(p.medio || "").toUpperCase()}</td>
                    <td>{origenPago(p)}</td>
                    <td style={{ textAlign: "right" }}>${$(p.importe)}</td>
                  </tr>
                ))}
                {!op.pagos?.length && (
                  <tr>
                    <td colSpan={3} className="text-muted">
                      Sin pagos
                    </td>
                  </tr>
                )}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={2} style={{ textAlign: "right" }}>
                    <b>Total pagos</b>
                  </td>
                  <td style={{ textAlign: "right" }}>
                    <b>${$(totPagos)}</b>
                  </td>
                </tr>
              </tfoot>
            </Table>

            <h6 className="mt-3">Retenciones</h6>
            <Table size="sm" bordered>
              <thead>
                <tr>
                  <th>Tipo</th>
                  <th style={{ textAlign: "right" }}>Base</th>
                  <th style={{ textAlign: "right" }}>%</th>
                  <th style={{ textAlign: "right" }}>Importe</th>
                  <th>Comp.</th>
                </tr>
              </thead>
              <tbody>
                {(op.retenciones || []).map((r, i) => (
                  <tr key={i}>
                    <td>{(r.tipo || "").toUpperCase()}</td>
                    <td style={{ textAlign: "right" }}>${$(r.base)}</td>
                    <td style={{ textAlign: "right" }}>
                      {Number(r.porcentaje || 0).toLocaleString("es-AR")}
                    </td>
                    <td style={{ textAlign: "right" }}>${$(r.importe)}</td>
                    <td>{r.comprobante || ""}</td>
                  </tr>
                ))}
                {!op.retenciones?.length && (
                  <tr>
                    <td colSpan={5} className="text-muted">
                      Sin retenciones
                    </td>
                  </tr>
                )}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={3} style={{ textAlign: "right" }}>
                    <b>Total retenciones</b>
                  </td>
                  <td style={{ textAlign: "right" }}>
                    <b>${$(totRets)}</b>
                  </td>
                  <td />
                </tr>
              </tfoot>
            </Table>

            <Row className="mt-2">
              <Col md={6}>
                <b>Σ Aplicado:</b> ${$(op?.totales?.aplicado || 0)}
              </Col>
              <Col md={6}>
                <b>Σ Neto pagado:</b> ${$(op?.totales?.netoPagado || 0)}
              </Col>
            </Row>

            {op.observaciones && (
              <>
                <h6 className="mt-3">Observaciones</h6>
                <div className="border rounded p-2">{op.observaciones}</div>
              </>
            )}
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={() => onHide(false)}>
          Cerrar
        </Button>
        <Button
          variant="primary"
          disabled={loading || !op}
          onClick={() => generarPDFOrdenPagoRemito(opId)}
        >
          Imprimir PDF
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
